import { gatherAllFormFields } from './gatherAllFormFields'
import { filterOutExcludedFields } from './filterOutExcludedFields'
import { type Schema, object } from 'yup'
import type { AppendFieldRulesType, SchemaEntryType } from '../_types/UseSchemaTypes'
import { generateSchemaEntry } from './generateSchemaEntry'
import { InputWithValidation } from '../../../types/EventDetailTypes'
import { logError } from '../../../../Helpers/logging'

type GenericFieldRuleType = (schema: SchemaEntryType) => SchemaEntryType

/**
 * @namespace
 * @description generate a yup validation schema, based on the inputs of a form
 *
 * All fields that should be validated are automatically gathered from the provided
 * form element. Input elements of type "submit" or "hidden" are skipped. Field names
 * provided in the excludeFieldNames parameter will also be skipped.
 *
 * For every input, a Schema entry will be constructed from the standard validation
 * constraints (min, max, minLength, maxLength, etc.)
 *
 * Additional field rules can be provided, that will be appended at the end of each
 * schema entry.
 *
 * @param formEl -- form element that will be used to gather the inputs from
 * @param appendableFieldRules -- field rules that will be appended to the appropriate
 * schema entries
 * @param excludeFieldNames -- field names that should be skipped when generating
 * the schema
 *
 * @returns a yup schema fro use in useSchema or useForm
 * */
export const generateSchema = (
    formEl: HTMLFormElement | null,
    appendableFieldRules?: AppendFieldRulesType,
    excludeFieldNames?: string[],
): Schema => {
    const allFormFields = gatherAllFormFields(formEl)
    const filteredFields = filterOutExcludedFields(allFormFields, excludeFieldNames)

    const schema: { [key: string]: SchemaEntryType } = {}
    filteredFields.forEach((value: Element) => {
        const field = value as InputWithValidation
        if (!field.name) {
            return
        }
        if (!!schema[field.name]) {
            return
        }

        const fieldRule = appendableFieldRules?.[field.name] as undefined | GenericFieldRuleType
        const entry = generateSchemaEntry(field)
        if (!!entry) schema[field.name] = entry
        try {
            if (!!entry && !!fieldRule) {
                schema[field.name] = fieldRule(entry)
            }
        } catch (error) {
            const message = [
                'Unable to append field rule.',
                'check if you provided the right types in the appendFieldRules callback functions',
                "and check if the methods you are using are available on the schema you're trying to extend",
            ].join(' ')
            logError(message)
        }
    })

    const objectSchema = object(schema)
    return objectSchema
}
